/**
 * BackToTop — 返回顶部按钮
 *
 * 功能：
 * - 滚动超过 300px 时渐显
 * - 点击平滑滚动回顶部
 * - 深海主题气泡样式 + 发光效果
 * - 滚动到底部时自动隐藏（避免遮挡内容）
 * - 移动端适配
 *
 * 使用示例：
 * <BackToTop />
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import { ArrowUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ★ 常量配置
const SCROLL_THRESHOLD = 300;        // 显示阈值（px）
const BOTTOM_OFFSET = 100;           // 距离底部多少px时隐藏
const ANIMATION_DURATION = 0.4;      // 动画时长（秒）

/**
 * Spring 动画配置（Apple 风格）
 */
const spring = {
  type: 'spring',
  bounce: 0.2,
  duration: 0.5,
};

/**
 * 返回顶部按钮组件
 */
const BackToTop = memo(() => {
  const [isVisible, setIsVisible] = useState(false);

  // ★ 计算是否显示按钮
  const calculateVisibility = useCallback(() => {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // 超过阈值 且 不在页面底部
    const shouldShow = scrollY > SCROLL_THRESHOLD &&
                       (documentHeight - scrollY - windowHeight) > BOTTOM_OFFSET;
    setIsVisible(shouldShow);
  }, []);

  // ★ 监听滚动事件
  useEffect(() => {
    // 初始计算
    calculateVisibility();

    // 节流优化
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          calculateVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', calculateVisibility, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateVisibility);
    };
  }, [calculateVisibility]);

  // ★ 滚动到顶部
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={spring}
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center group"
          style={{
            background: 'rgba(8, 18, 36, 0.85)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(100, 180, 230, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(80, 160, 220, 0.1)',
          }}
          aria-label="返回顶部"
          title="返回顶部"
        >
          {/* 发光效果 */}
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: 'radial-gradient(circle at center, rgba(80, 160, 220, 0.3) 0%, transparent 70%)',
            }}
          />

          {/* 图标 */}
          <ArrowUp
            className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:-translate-y-0.5"
            style={{ color: 'rgba(180, 220, 255, 0.9)' }}
          />

          {/* 脉冲动画点 */}
          <span
            className="absolute top-1 right-1 w-2 h-2 rounded-full animate-pulse"
            style={{ background: 'rgba(80, 200, 255, 0.8)' }}
          />
        </motion.button>
      )}
    </AnimatePresence>
  );
});

BackToTop.displayName = 'BackToTop';

export default BackToTop;
